/* eslint-disable react-hooks/exhaustive-deps */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  ReactNode,
  Dispatch,
  SetStateAction,
  useRef,
} from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { SupabaseService } from '@libs/utils';
import { ChatRoomRow, MessageRow } from '@libs/models';
import { routes } from '@carcareers/utils';
import { useAuth } from '@carcareers/hooks';

interface ChatContextProps {
  chatRooms: Record<string, ChatRoomRow>;
  messages: Record<string, MessageRow>;
  loadingRooms: boolean;
  loadingMessages: boolean;
  sending: boolean;
  hasMoreRooms: boolean;
  hasMoreMessages: boolean;
  hasUnread: boolean;
  selectedRoom: ChatRoomRow | null;
  setSelectedRoom: Dispatch<SetStateAction<ChatRoomRow | null>>;
  loadChatRooms: () => Promise<void>;
  loadMessages: () => Promise<void>;
  sendMessage: (text: string) => Promise<void>;
  employerChatAction: (
    postId?: number,
    jobseekerUserId?: number,
    jobseekerName?: string,
    employerName?: string,
    jobTitle?: string,
    rooftopName?: string,
  ) => Promise<void>;
}

const ChatContext = createContext<ChatContextProps | null>(null);

export const useChat = () => {
  const context = useContext(ChatContext);

  if (!context) {
    throw new Error('useChatContext must be used within a ChatProvider');
  }

  return context;
};

interface ChatProviderProps {
  children: ReactNode;
  supabaseUrl: string;
  supabaseKey: string;
}

export const ChatProvider: React.FC<ChatProviderProps> = ({ children, supabaseUrl, supabaseKey }) => {
  const [chatRooms, setChatRooms] = useState<Record<string, ChatRoomRow>>({});
  const [messages, setMessages] = useState<Record<string, MessageRow>>({});
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [hasMoreRooms, setHasMoreRooms] = useState(true);
  const [messagePage, setMessagePage] = useState(0);
  const [roomPage, setRoomPage] = useState(0);
  const [totalRooms, setTotalRooms] = useState<number | null>(null);
  const [totalMessages, setTotalMessages] = useState<number | null>(null);

  const [selectedRoom, setSelectedRoom] = useState<ChatRoomRow | null>(null);
  const [hasUnread, setHasUnread] = useState(false);
  const [sending, setSending] = useState(false);

  const unreadUpdatedRef = useRef<Record<number, boolean>>({});

  const { pathname } = useLocation();
  const { id: chatRoomId } = useParams();
  const navigate = useNavigate();

  const { user } = useAuth();
  const isEmployer = useMemo(() => user?.role === 'employer', [user?.role]);
  const isOnChatPage = useMemo(
    () => pathname.includes(routes.EMPLOYER_CHAT) || pathname.includes(routes.JOBSEEKER_CHAT),
    [pathname],
  );
  const supabase = useMemo(() => SupabaseService.getInstance(supabaseUrl, supabaseKey), [supabaseUrl, supabaseKey]);

  const loadHasUnread = async () => {
    try {
      const unread = await supabase.findChatRoomsWithUnreadMessages(user?.id, user?.role);
      setHasUnread(unread);
    } catch (error) {
      setHasUnread(false);
    }
  };

  const loadChatRooms = async () => {
    if (loadingRooms || !user?.id || !user?.role) return;

    setLoadingRooms(true);

    try {
      const limit = 10;

      if (totalRooms !== null && roomPage * limit > totalRooms) {
        setHasMoreRooms(false);
        return;
      }

      const roomData = await supabase.getChatRoomsForUser(user?.id, user?.role, limit, roomPage);
      const newRooms = roomData.items.reduce((acc: Record<string, ChatRoomRow>, room) => {
        acc[`${room.id}-${room.job_title}`] = room;
        return acc;
      }, {});
      const allRooms = { ...chatRooms, ...newRooms };
      const hasMore = roomData.total > Object.keys(allRooms).length;

      setChatRooms(allRooms);
      setTotalRooms(roomData.total);
      setHasMoreRooms(hasMore);

      if (hasMore) {
        setRoomPage(roomData.page + 1);
      }

      if (chatRoomId) {
        const roomToSelect = roomData.items.find((room) => room.id === parseInt(chatRoomId));

        if (roomToSelect) {
          setSelectedRoom(roomToSelect);

          const unread = isEmployer ? roomToSelect.employer_has_unread : roomToSelect.jobseeker_has_unread;
          setHasUnread(unread || false);
        }
      } else if (roomData.items.length > 0 && !selectedRoom) {
        setSelectedRoom(roomData.items[0]);
      }
    } catch (error) {
      setChatRooms({});
      setTotalRooms(0);
      setHasMoreRooms(false);
      setRoomPage(0);
    } finally {
      setLoadingRooms(false);
    }
  };

  const loadMessages = async () => {
    if (!selectedRoom) return;

    setLoadingMessages(true);

    try {
      const limit = 15;

      if (totalMessages !== null && messagePage * limit > totalMessages) {
        setHasMoreMessages(false);
        return;
      }

      const messageData = await supabase.getMessages(selectedRoom.id, limit, messagePage);
      const newMessages = messageData.items.reduce((acc: Record<string, MessageRow>, message) => {
        acc[message.id.toString()] = message;
        return acc;
      }, {});
      const allMessages = { ...messages, ...newMessages };
      const hasMore = messageData.total > Object.keys(allMessages).length;

      setMessages(allMessages);
      setTotalMessages(messageData.total);
      setHasMoreMessages(hasMore);

      if (hasMore) {
        setMessagePage(messageData.page + 1);
      }

      // Only update unread status if it hasn't been updated yet for this room
      if (!unreadUpdatedRef.current[selectedRoom.id]) {
        if (isEmployer) {
          await supabase.updateChatRoom(selectedRoom.id, {
            employer_has_unread: false,
          });
        } else {
          await supabase.updateChatRoom(selectedRoom.id, {
            jobseeker_has_unread: false,
          });
        }
        unreadUpdatedRef.current[selectedRoom.id] = true;
      }
    } catch (error) {
      console.error('Error loading messages:', error);
    } finally {
      setLoadingMessages(false);
    }
  };

  const sendMessage = async (text: string) => {
    if (!selectedRoom || !text.trim()) return;

    setSending(true);

    try {
      if (isEmployer) {
        await supabase.updateChatRoom(selectedRoom.id, {
          jobseeker_has_unread: true,
        });
      } else {
        await supabase.updateChatRoom(selectedRoom.id, {
          employer_has_unread: true,
        });
      }

      const newMessage = await supabase.sendMessage(selectedRoom.id, text.trim(), user?.id);

      if (newMessage) {
        // Add the new message to the messages array
        setMessages((prev) => ({ ...prev, [newMessage.id]: newMessage }));

        // Also update the selected room's last message properties locally
        // This prevents unnecessary reloading of the room data
        setSelectedRoom((prev) => {
          if (prev) {
            return {
              ...prev,
              last_message_id: newMessage.id,
              last_message_text: text.trim(),
              last_message_sent_at: newMessage.sent_at,
            };
          }
          return prev;
        });

        // Also update the chat room in the list
        setChatRooms((prev) => ({
          ...prev,
          [`${selectedRoom.id}-${selectedRoom.job_title}`]: {
            ...selectedRoom,
            last_message_id: newMessage.id,
            last_message_text: text.trim(),
            last_message_sent_at: newMessage.sent_at,
          },
        }));
      }
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSending(false);
    }
  };

  const getChatRoomById = async (id?: number) => {
    try {
      const room = await supabase.getChatRoomById(id);

      if (room) {
        // Reset the unread status tracking when changing rooms
        unreadUpdatedRef.current = {};

        setSelectedRoom(room);
        setMessagePage(0);
        setTotalMessages(null);
        setHasMoreMessages(true);
        setMessages({});
      }
    } catch (error) {
      setSelectedRoom(null);
    }
  };

  const createChatRoom = async (
    postId?: number,
    jobseekerUserId?: number,
    jobseekerName?: string,
    jobTitle?: string,
    rooftopName?: string,
  ): Promise<ChatRoomRow> => {
    if (user?.role !== 'employer') {
      throw new Error('You are not authorized to create a chat room');
    }

    try {
      const newRoom = await supabase.createChatRoom(
        postId,
        jobseekerUserId,
        user?.id,
        jobseekerName,
        `${user?.firstName} ${user?.lastName}`,
        jobTitle,
        rooftopName,
      );

      if (newRoom) {
        setChatRooms((prev) => ({ ...prev, [`${newRoom.id}-${newRoom.job_title}`]: newRoom }));
      }

      return newRoom;
    } catch (error) {
      console.error('Error creating chat room:', error);
      throw error;
    }
  };

  const employerChatAction = async (
    postId?: number,
    jobseekerUserId?: number,
    jobseekerName?: string,
    jobTitle?: string,
    rooftopName?: string,
  ) => {
    try {
      const existingChatRoom = await supabase.getChatRoomForEmployerPostAndJobseeker(postId, jobseekerUserId, user?.id);

      if (existingChatRoom) {
        navigate(`/${routes.EMPLOYER_HOME}/${routes.EMPLOYER_CHAT}/${existingChatRoom.id}`);
      } else {
        const newRoom = await createChatRoom(postId, jobseekerUserId, jobseekerName, jobTitle, rooftopName);
        await loadChatRooms();

        setSelectedRoom(newRoom);
        navigate(`/${routes.EMPLOYER_HOME}/${routes.EMPLOYER_CHAT}/${newRoom.id}`);
      }
    } catch (error) {
      console.error('Error in employerChatAction:', error);
    }
  };

  // Loads hasUnread
  useEffect(() => {
    if (!isOnChatPage && user?.id) {
      loadHasUnread();
    }
  }, [isOnChatPage, supabase, user?.id]);

  // Loads Chat Rooms
  useEffect(() => {
    if (!isOnChatPage || !user?.id) return;

    loadChatRooms();
  }, [isOnChatPage, user?.id]);

  // Changes the selected room when the chatRoomId changes
  useEffect(() => {
    if (!isOnChatPage || !chatRoomId) return;

    getChatRoomById(parseInt(chatRoomId));
  }, [chatRoomId, isOnChatPage]);

  // Loads the messages when the selected room changes
  useEffect(() => {
    if (!isOnChatPage || !selectedRoom) return;

    loadMessages();
  }, [isOnChatPage, selectedRoom]);

  // Subscribe to message updates
  useEffect(() => {
    if (!isOnChatPage || !selectedRoom) return;

    const subscription = supabase.subscribeToMessages(selectedRoom.id, async (payload) => {
      const newMessage = payload.new;
      setMessages((prev) => ({ ...prev, [newMessage.id.toString()]: newMessage }));

      // Only mark as read if the message is from the other user
      if (newMessage.sender_user_id !== user?.id) {
        if (isEmployer) {
          await supabase.updateChatRoom(selectedRoom.id, {
            employer_has_unread: false,
          });
        } else {
          await supabase.updateChatRoom(selectedRoom.id, {
            jobseeker_has_unread: false,
          });
        }
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [isOnChatPage, supabase, selectedRoom, user?.id]);

  // Subscribe to room updates
  useEffect(() => {
    if (!isOnChatPage || !user?.id) return;

    const subscription = supabase.subscribeToRoomUpdates(user.id, async (payload) => {
      if (payload.eventType === 'UPDATE') {
        const updatedRoom = payload.new;

        if (updatedRoom) {
          setChatRooms((prev) => {
            const newRooms = { ...prev };
            newRooms[`${updatedRoom.id}-${updatedRoom.job_title}`] = updatedRoom;
            return newRooms;
          });

          // Only update selectedRoom if it's not going to trigger a re-render cycle
          if (selectedRoom && selectedRoom.id === updatedRoom.id) {
            // Check if there are actual changes that would affect UI
            const currentHasUnread = isEmployer ? selectedRoom.employer_has_unread : selectedRoom.jobseeker_has_unread;
            const updatedHasUnread = isEmployer ? updatedRoom.employer_has_unread : updatedRoom.jobseeker_has_unread;

            // Only update if there's a meaningful difference
            if (currentHasUnread !== updatedHasUnread || selectedRoom.last_message_id !== updatedRoom.last_message_id) {
              setSelectedRoom(updatedRoom);
            }
          }
        }
      }
    });

    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, [isOnChatPage, supabase, user?.id]); // Removed selectedRoom from dependency array

  const value = {
    chatRooms,
    loadingRooms,
    selectedRoom,
    messages,
    loadingMessages,
    sending,
    hasMoreRooms,
    hasMoreMessages,
    hasUnread,
    loadChatRooms,
    loadMessages,
    setSelectedRoom,
    sendMessage,
    createChatRoom,
    employerChatAction,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export default ChatContext;
